import { supabase } from '../lib/supabase';
import { toDueISO } from '../utils/followUpDates';

const SELECT =
  '*, card_leads(full_name, company_name, job_title, is_important, status, contact_id), contacts(full_name, company, job_title), moments(id, title)';

const fail = (error, fallback) => {
  throw new Error(error?.message || fallback);
};

export const followUpService = {
  listOpen: async (userId) => {
    const { data, error } = await supabase
      .from('connection_reminders')
      .select(SELECT)
      .eq('user_id', userId)
      .eq('is_completed', false)
      .order('due_date', { ascending: true });
    if (error) fail(error, 'Could not load follow-ups.');
    return data || [];
  },

  listCompleted: async (userId, limit = 50) => {
    const { data, error } = await supabase
      .from('connection_reminders')
      .select(SELECT)
      .eq('user_id', userId)
      .eq('is_completed', true)
      .order('completed_at', { ascending: false, nullsFirst: false })
      .limit(limit);
    if (error) fail(error, 'Could not load completed follow-ups.');
    return data || [];
  },

  listForPerson: async ({ userId, contactId, connectionId }) => {
    const parts = [];
    if (contactId) parts.push(`contact_id.eq.${contactId}`);
    if (connectionId) parts.push(`connection_id.eq.${connectionId}`);
    if (parts.length === 0) return [];
    const { data, error } = await supabase
      .from('connection_reminders')
      .select(SELECT)
      .eq('user_id', userId)
      .or(parts.join(','));
    if (error) fail(error, 'Could not load follow-ups.');
    return data || [];
  },

  create: async ({ userId, contactId, connectionId, day, note, momentId, source = 'manual' }) => {
    let connId = connectionId || null;
    if (!connId && contactId) {
      const { data } = await supabase
        .from('card_leads')
        .select('id')
        .eq('user_id', userId)
        .eq('contact_id', contactId)
        .limit(1);
      connId = data?.[0]?.id || null;
    }
    if (!connId && !contactId) throw new Error('Choose a person for this follow-up.');

    const { data, error } = await supabase
      .from('connection_reminders')
      .insert({
        user_id: userId,
        connection_id: connId,
        contact_id: contactId || null,
        moment_id: momentId || null,
        due_date: toDueISO(day),
        note: (note || '').trim(),
        source
      })
      .select(SELECT)
      .single();
    if (error) fail(error, 'Could not save the follow-up.');
    return data;
  },

  update: async (id, userId, { day, note }) => {
    const { data, error } = await supabase
      .from('connection_reminders')
      .update({ due_date: toDueISO(day), note: (note || '').trim() })
      .eq('id', id)
      .eq('user_id', userId)
      .select(SELECT)
      .single();
    if (error) fail(error, 'Could not update the follow-up.');
    return data;
  },

  complete: async (id, userId, completionNote) => {
    const { data, error } = await supabase
      .from('connection_reminders')
      .update({
        is_completed: true,
        completed_at: new Date().toISOString(),
        completion_note: (completionNote || '').trim() || null
      })
      .eq('id', id)
      .eq('user_id', userId)
      .select(SELECT)
      .single();
    if (error) fail(error, 'Could not complete the follow-up.');
    return data;
  },

  reopen: async (id, userId) => {
    const { data, error } = await supabase
      .from('connection_reminders')
      .update({ is_completed: false, completed_at: null })
      .eq('id', id)
      .eq('user_id', userId)
      .select(SELECT)
      .single();
    if (error) fail(error, 'Could not reopen the follow-up.');
    return data;
  },

  remove: async (id, userId) => {
    const { error } = await supabase
      .from('connection_reminders')
      .delete()
      .eq('id', id)
      .eq('user_id', userId);
    if (error) fail(error, 'Could not delete the follow-up.');
  }
};